/* SMOON — carte des boutiques (sections/store-locator.liquid).
 *
 * Les boutiques arrivent de page.boutiques-data.liquid quand la section
 * approche de l'écran. Une boutique y est un tableau compact (voir l'en-tête
 * de ops/boutiques/Code.gs) :
 *   [id, nom, enseigne, adresse, complément, cp, ville, pays, lat, lng, gammes, tél, e-mail, site]
 * enseigne = indice dans index.enseignes (-1 : indépendante),
 * gammes = masque de bits sur index.gammes.
 *
 * La carte est MapLibre (thème, assets/maplibre-gl.js) sur un fond OpenFreeMap.
 * Desktop : chargée avec les données. Mobile : au premier appui sur « Carte ».
 */
(() => {
  if (window.SmoonStoreLocator) return;

  const MOBILE = window.matchMedia('(max-width: 749px)');
  const FRANCE = [[-5.2, 42.2], [8.3, 51.2]];

  let maplibrePromise = null;
  function loadMaplibre(js, css) {
    if (window.maplibregl) return Promise.resolve(window.maplibregl);
    if (!maplibrePromise) {
      maplibrePromise = new Promise((resolve, reject) => {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = css;
        document.head.appendChild(link);
        const s = document.createElement('script');
        s.src = js;
        s.async = true;
        s.onload = () => resolve(window.maplibregl);
        s.onerror = () => { maplibrePromise = null; reject(new Error('maplibre')); };
        document.head.appendChild(s);
      });
    }
    return maplibrePromise;
  }

  const dataCache = {};
  function loadData(src) {
    if (!dataCache[src]) {
      dataCache[src] = fetch(src, { credentials: 'omit' })
        .then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); })
        .catch((e) => { delete dataCache[src]; throw e; });
    }
    return dataCache[src];
  }

  const norm = (s) => String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ').replace(/\bst\b/g, 'saint').replace(/\bste\b/g, 'sainte').trim();

  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  function km(a, b, c, d) {
    const r = Math.PI / 180;
    const h = Math.sin(((c - a) * r) / 2) ** 2 + Math.cos(a * r) * Math.cos(c * r) * Math.sin(((d - b) * r) / 2) ** 2;
    return 12742 * Math.asin(Math.sqrt(h));
  }

  const fill = (tpl, vals) => String(tpl || '').replace(/\[(\w+)\]/g, (m, k) => (k in vals ? vals[k] : m));

  const ICONS = {
    dir: '<svg width="15" height="15" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2l10 10-10 10L2 12z"/><path d="M9 14v-3h5M12 8.5l2.5 2.5L12 13.5"/></svg>',
    phone: '<svg width="15" height="15" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2"/></svg>',
    mail: '<svg width="15" height="15" viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="5" width="18" height="14"/><path d="M3 7l9 6 9-6"/></svg>',
    web: '<svg width="15" height="15" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18"/></svg>',
    map: '<svg width="15" height="15" viewBox="0 0 24 24" aria-hidden="true"><path d="M9 4L3 6v14l6-2 6 2 6-2V4l-6 2z"/><path d="M9 4v14M15 6v14"/></svg>',
    x: '<svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>',
  };

  class StoreLocator {
    constructor(root) {
      this.root = root;
      this.cfg = JSON.parse(root.querySelector('[data-sl-config]').textContent);
      this.t = this.cfg.t;
      this.lang = document.documentElement.lang || 'fr';
      this.fmt1 = new Intl.NumberFormat(this.lang, { maximumFractionDigits: 1, minimumFractionDigits: 1 });
      this.fmt0 = new Intl.NumberFormat(this.lang, { maximumFractionDigits: 0 });
      try { this.regions = new Intl.DisplayNames([this.lang], { type: 'region' }); } catch (e) { this.regions = null; }
      this.pageSize = parseInt(root.dataset.pageSize, 10) || 10;

      const q = (sel) => root.querySelector(sel);
      this.el = {
        form: q('[data-sl-form]'), input: q('[data-sl-q]'), clear: q('[data-sl-clear]'), locate: q('[data-sl-locate]'),
        radius: q('[data-sl-radius]'), brand: q('[data-sl-brand]'), body: q('.sl__body'), welcome: q('[data-sl-welcome]'),
        results: q('[data-sl-results]'), count: q('[data-sl-count]'), list: q('[data-sl-list]'), more: q('[data-sl-more]'),
        status: q('[data-sl-status]'), map: q('[data-sl-map]'), canvas: q('[data-sl-canvas]'), card: q('[data-sl-card]'),
        viewCount: q('[data-sl-view-count]'),
      };

      this.state = { origin: null, radius: parseInt(this.el.radius.value, 10), gamme: null, brand: '', shown: this.pageSize, selected: null };
      this.stores = null;
      this.results = [];
      this.map = null;
      this.pins = [];
      this.bind();

      const params = new URLSearchParams(location.search);
      this.pending = params.get('ville') || params.get('q') || '';
      if (this.pending) this.el.input.value = this.pending;
      this.toggleClear();

      /* Données dès que la section approche de l'écran, ou à la première interaction. */
      const io = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) { io.disconnect(); this.start(); }
      }, { rootMargin: '600px 0px' });
      io.observe(root);
      if (this.pending) this.start();
    }

    /* ------------------------------------------------------------ données */

    start() {
      if (this.ready) return this.ready;
      this.ready = loadData(this.root.dataset.src).then((d) => {
        const idx = d.index || {};
        this.gammes = idx.gammes || this.cfg.gammes;
        this.enseignes = idx.enseignes || this.cfg.enseignes;
        this.stores = [].concat(...(d.morceaux || [])).map((r) => ({
          id: r[0], nom: r[1], e: r[2] == null ? -1 : r[2], adr: r[3] || '', comp: r[4] || '', cp: r[5] || '', ville: r[6] || '',
          pays: r[7] || '', lat: r[8], lng: r[9], g: r[10] || 0, tel: r[11] || '', mail: r[12] || '', web: r[13] || '',
        })).filter((s) => typeof s.lat === 'number' && typeof s.lng === 'number');
        this.byId = new Map(this.stores.map((s) => [s.id, s]));
        if (!MOBILE.matches) this.ensureMap();
        if (this.pending) { const p = this.pending; this.pending = ''; this.search(p); } else this.render();
      }).catch(() => {
        this.ready = null;
        this.showStatus(`<p>${esc(this.t.error)}</p>`);
      });
      return this.ready;
    }

    filtered() {
      const { gamme, brand } = this.state;
      const bit = gamme === null ? 0 : 1 << gamme;
      const e = brand === '' ? null : parseInt(brand, 10);
      return this.stores.filter((s) => (!bit || (s.g & bit)) && (e === null || s.e === e));
    }

    /* --------------------------------------------------------- événements */

    bind() {
      const { el } = this;
      el.form.addEventListener('submit', (ev) => {
        ev.preventDefault();
        const v = el.input.value.trim();
        if (v) this.search(v);
        else el.input.focus();
      });
      el.input.addEventListener('input', () => this.toggleClear());
      el.input.addEventListener('focus', () => this.start(), { once: true });
      el.clear.addEventListener('click', () => {
        el.input.value = '';
        this.toggleClear();
        this.state.origin = null;
        this.state.selected = null;
        this.setUrl('');
        this.render();
        el.input.focus();
      });
      el.locate.addEventListener('click', () => this.locate());
      el.radius.addEventListener('change', () => {
        this.state.radius = parseInt(el.radius.value, 10);
        this.state.shown = this.pageSize;
        this.render();
      });
      el.brand.addEventListener('change', () => {
        this.state.brand = el.brand.value;
        this.state.shown = this.pageSize;
        this.render();
      });
      this.root.querySelectorAll('[data-sl-gamme]').forEach((b) => b.addEventListener('click', () => {
        this.root.querySelectorAll('[data-sl-gamme]').forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
        this.state.gamme = b.dataset.slGamme === '' ? null : parseInt(b.dataset.slGamme, 10);
        this.state.shown = this.pageSize;
        this.render();
      }));
      this.root.querySelectorAll('[data-sl-view]').forEach((b) => b.addEventListener('click', () => this.setView(b.dataset.slView)));
      el.more.addEventListener('click', () => {
        const first = this.state.shown;
        this.state.shown += this.pageSize;
        this.render();
        const next = el.list.children[first];
        if (next) next.querySelector('.sl__name').focus();
      });
      this.root.addEventListener('click', (ev) => {
        const city = ev.target.closest('[data-sl-city]');
        if (city) {
          ev.preventDefault();
          const v = this.cfg.villes[parseInt(city.dataset.slCity, 10)];
          if (!v) return;
          el.input.value = v[0];
          this.toggleClear();
          this.start().then(() => this.setOrigin({ lat: v[2], lng: v[3], label: v[0] }, v[0]));
          this.root.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
        const pick = ev.target.closest('[data-sl-pick]');
        if (pick) { this.select(pick.dataset.slPick, 'list'); return; }
        const onmap = ev.target.closest('[data-sl-onmap]');
        if (onmap) { this.setView('map'); this.select(onmap.dataset.slOnmap, 'list'); return; }
        const widen = ev.target.closest('[data-sl-widen]');
        if (widen) {
          el.radius.value = widen.dataset.slWiden;
          el.radius.dispatchEvent(new Event('change'));
          return;
        }
        if (ev.target.closest('[data-sl-card-close]')) { this.state.selected = null; this.renderSelection(); }
      });
      MOBILE.addEventListener('change', () => {
        if (!MOBILE.matches) { this.setView('list'); this.ensureMap(); }
        if (this.map) this.map.resize();
      });
    }

    toggleClear() { this.el.clear.hidden = !this.el.input.value; }

    setUrl(v) {
      const u = new URL(location.href);
      u.searchParams.delete('q');
      if (v) u.searchParams.set('ville', v); else u.searchParams.delete('ville');
      history.replaceState(history.state, '', u);
    }

    setView(view) {
      this.el.body.dataset.view = view;
      this.root.querySelectorAll('[data-sl-view]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.slView === view)));
      if (view === 'map') this.ensureMap().then(() => this.map && this.map.resize());
    }

    /* ---------------------------------------------------------- recherche */

    async search(query) {
      this.showStatus(`<p>${esc(this.t.loading)}</p>`);
      await this.start();
      if (!this.stores) return;
      const n = norm(query);

      /* 1. Une ville de la liste, ou une ville où l'on a des boutiques : sans réseau. */
      const v = this.cfg.villes.find((x) => norm(x[0]) === n);
      if (v) return this.setOrigin({ lat: v[2], lng: v[3], label: v[0] }, query);
      const memes = this.stores.filter((s) => norm(s.ville) === n);
      if (memes.length) {
        const lat = memes.reduce((a, s) => a + s.lat, 0) / memes.length;
        const lng = memes.reduce((a, s) => a + s.lng, 0) / memes.length;
        return this.setOrigin({ lat, lng, label: memes[0].ville }, query);
      }

      /* 2. Géocodage : Géoplateforme (IGN) pour la France, Photon ailleurs. */
      try {
        const fr = await this.geocodeFrance(query);
        if (fr) return this.setOrigin(fr, query);
        const w = await this.geocodeWorld(query);
        if (w) return this.setOrigin(w, query);
      } catch (e) { /* on tombe sur le message ci-dessous */ }
      this.showStatus(`<p>${esc(this.t.not_found)}</p>`);
    }

    async geocodeFrance(q) {
      const u = new URL('https://data.geopf.fr/geocodage/search');
      u.searchParams.set('q', q);
      u.searchParams.set('limit', '1');
      const r = await fetch(u);
      if (!r.ok) return null;
      const f = ((await r.json()).features || [])[0];
      if (!f || f.properties.score < 0.6) return null;
      return { lng: f.geometry.coordinates[0], lat: f.geometry.coordinates[1], label: f.properties.label };
    }

    async geocodeWorld(q) {
      const u = new URL('https://photon.komoot.io/api/');
      u.searchParams.set('q', q);
      u.searchParams.set('limit', '1');
      if (['de', 'en', 'fr'].includes(this.lang.slice(0, 2))) u.searchParams.set('lang', this.lang.slice(0, 2));
      const r = await fetch(u);
      if (!r.ok) return null;
      const f = ((await r.json()).features || [])[0];
      if (!f) return null;
      const p = f.properties;
      return { lng: f.geometry.coordinates[0], lat: f.geometry.coordinates[1], label: [p.name, p.city, p.country].filter(Boolean).join(', ') };
    }

    locate() {
      if (!navigator.geolocation) { this.showStatus(`<p>${esc(this.t.geo_denied)}</p>`); return; }
      const b = this.el.locate;
      b.setAttribute('aria-busy', 'true');
      this.start();
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          b.removeAttribute('aria-busy');
          this.el.input.value = '';
          this.toggleClear();
          this.start().then(() => this.setOrigin({ lat: pos.coords.latitude, lng: pos.coords.longitude, label: this.t.geo_label, me: true }, ''));
        },
        () => { b.removeAttribute('aria-busy'); this.showStatus(`<p>${esc(this.t.geo_denied)}</p>`); },
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 },
      );
    }

    setOrigin(origin, query) {
      this.state.origin = origin;
      this.state.shown = this.pageSize;
      this.state.selected = null;
      this.setUrl(query);
      this.render({ fit: true });
    }

    /* -------------------------------------------------------------- rendu */

    showStatus(html) {
      this.el.status.innerHTML = html;
      this.el.status.hidden = false;
      this.el.welcome.hidden = true;
      this.el.results.hidden = true;
    }

    render(opts = {}) {
      if (!this.stores) return;
      const { origin, radius } = this.state;
      const all = this.filtered();
      this.el.status.hidden = true;

      if (!origin) {
        this.results = [];
        this.el.welcome.hidden = false;
        this.el.results.hidden = true;
        this.el.viewCount.textContent = '';
        this.updateMap(all, { fit: opts.fit !== false && !this.fitted, france: true });
        return;
      }

      const withD = all.map((s) => ({ s, d: km(origin.lat, origin.lng, s.lat, s.lng) })).sort((a, b) => a.d - b.d);
      const inside = withD.filter((x) => x.d <= radius);
      this.results = inside;
      this.el.welcome.hidden = true;
      this.el.viewCount.textContent = ` (${this.fmt0.format(inside.length)})`;

      if (!inside.length) {
        this.el.results.hidden = true;
        let html = `<p>${esc(fill(this.t.empty, { r: radius }))}</p>`;
        if (withD.length) {
          const d = withD[0].d;
          html += `<p>${esc(fill(this.t.nearest, { d: this.dist(d) }))}</p>`;
          const next = this.cfg.radii.find((r) => r >= d);
          if (next && next > radius) {
            html += `<p><button class="sl__btn sl__btn--outline" type="button" data-sl-widen="${next}">${esc(fill(this.t.widen, { r: next }))}</button></p>`;
          }
        }
        this.showStatus(html);
        this.updateMap(all, { fit: opts.fit, around: withD.slice(0, 1).map((x) => x.s) });
        return;
      }

      const shown = inside.slice(0, this.state.shown);
      this.el.results.hidden = false;
      this.el.count.textContent = inside.length === 1
        ? fill(this.t.count_one, { r: radius })
        : fill(this.t.count, { n: this.fmt0.format(inside.length), r: radius });
      this.el.list.innerHTML = shown.map((x, i) => `<li>${this.item(x.s, x.d, i + 1)}</li>`).join('');
      const left = inside.length - shown.length;
      this.el.more.hidden = left <= 0;
      this.el.more.parentElement.hidden = left <= 0;
      if (left > 0) this.el.more.textContent = fill(this.t.more, { n: Math.min(left, this.pageSize) });
      if (opts.fit) this.el.list.scrollTop = 0;
      this.updateMap(all, { fit: opts.fit, around: shown.map((x) => x.s), numbered: shown.map((x) => x.s) });
      this.renderSelection();
    }

    dist(d) { return (d < 10 ? this.fmt1.format(d) : this.fmt0.format(d)) + ' km'; }

    item(s, d, n) {
      const t = this.t;
      const brand = s.e >= 0 ? this.enseignes[s.e] : t.independent;
      const pays = s.pays && s.pays !== 'FR' ? (this.regions ? this.regions.of(s.pays) : s.pays) : '';
      const ligne2 = [s.cp, s.ville].filter(Boolean).join(' ');
      const adr = [s.adr, s.comp, ligne2, pays].filter(Boolean).map(esc).join(', ');
      const tags = this.gammes.filter((g, i) => s.g & (1 << i)).map((g) => `<span class="sl__tag">${esc(g)}</span>`).join('');
      const dest = encodeURIComponent([s.nom, s.adr, ligne2, pays].filter(Boolean).join(', '));
      const acts = [`<a class="sl__action" href="https://www.google.com/maps/dir/?api=1&amp;destination=${dest}" target="_blank" rel="noopener">${ICONS.dir}${esc(t.directions)}</a>`];
      if (s.tel) acts.push(`<a class="sl__action" href="tel:${esc(s.tel.replace(/[^\d+]/g, ''))}">${ICONS.phone}${esc(t.call)}</a>`);
      if (s.mail) acts.push(`<a class="sl__action" href="mailto:${esc(s.mail)}">${ICONS.mail}${esc(t.email)}</a>`);
      if (s.web) acts.push(`<a class="sl__action" href="${esc(/^https?:/.test(s.web) ? s.web : 'https://' + s.web)}" target="_blank" rel="noopener">${ICONS.web}${esc(t.website)}</a>`);
      if (n) acts.push(`<button class="sl__action sl__onmap" type="button" data-sl-onmap="${esc(s.id)}">${ICONS.map}${esc(t.show_on_map)}</button>`);
      return `<article class="sl__item" data-sl-item="${esc(s.id)}"${this.state.selected === s.id ? ' aria-current="true"' : ''}>`
        + (n ? `<span class="sl__num" aria-hidden="true">${n}</span>` : '')
        + '<div class="sl__item-body"><div class="sl__item-top">'
        + `<h3 class="sl__h"><button class="sl__name" type="button" data-sl-pick="${esc(s.id)}">${esc(s.nom)}</button></h3>`
        + (d != null ? `<span class="sl__dist">${this.dist(d)}</span>` : '')
        + `</div><span class="sl__brandname">${esc(brand)}</span>`
        + `<address class="sl__addr">${adr}</address>`
        + (tags ? `<div class="sl__tags">${tags}</div>` : '')
        + `<div class="sl__actions">${acts.join('')}</div></div></article>`;
    }

    select(id, from) {
      this.state.selected = this.state.selected === id && from === 'map-toggle' ? null : id;
      this.renderSelection(from);
    }

    renderSelection(from) {
      const id = this.state.selected;
      this.el.list.querySelectorAll('[data-sl-item]').forEach((a) => {
        if (a.dataset.slItem === id) a.setAttribute('aria-current', 'true'); else a.removeAttribute('aria-current');
      });
      this.pins.forEach((p) => p.el.classList.toggle('is-selected', p.id === id));

      const s = id ? this.byId.get(id) : null;
      const inList = s && this.results.some((x) => x.s.id === id);
      /* Fiche sur la carte : toujours en mobile, en desktop seulement pour une
         boutique qui n'est pas dans la liste (point cliqué hors des résultats). */
      const card = s && (MOBILE.matches || !inList) && from !== 'list-desktop';
      if (card) {
        const d = this.state.origin ? km(this.state.origin.lat, this.state.origin.lng, s.lat, s.lng) : null;
        this.el.card.innerHTML = `<button class="sl__card-close" type="button" aria-label="${esc(this.t.close)}" data-sl-card-close>${ICONS.x}</button>${this.item(s, d, 0)}`;
        this.el.card.hidden = false;
      } else {
        this.el.card.hidden = true;
        this.el.card.innerHTML = '';
      }

      if (s && from === 'map' && inList && !MOBILE.matches) {
        const a = this.el.list.querySelector(`[data-sl-item="${CSS.escape(id)}"]`);
        if (a) {
          const box = this.el.list;
          box.scrollTo({ top: a.parentElement.offsetTop - box.offsetTop - 8, behavior: 'smooth' });
        }
      }
      if (s && this.map && (from === 'list' || from === 'map')) {
        const b = this.map.getBounds();
        const pad = MOBILE.matches ? 0.25 : 0.12;
        const dx = (b.getEast() - b.getWest()) * pad;
        const dy = (b.getNorth() - b.getSouth()) * pad;
        const visible = s.lng > b.getWest() + dx && s.lng < b.getEast() - dx && s.lat > b.getSouth() + dy && s.lat < b.getNorth() - dy;
        if (!visible || MOBILE.matches) {
          this.map.easeTo({ center: [s.lng, s.lat], zoom: Math.max(this.map.getZoom(), 13), offset: MOBILE.matches ? [0, -90] : [0, 0], duration: 600 });
        }
      }
    }

    /* -------------------------------------------------------------- carte */

    ensureMap() {
      if (this.mapReady) return this.mapReady;
      this.mapReady = loadMaplibre(this.root.dataset.maplibreJs, this.root.dataset.maplibreCss).then((ml) => new Promise((resolve) => {
        const t = this.t;
        const map = new ml.Map({
          container: this.el.canvas,
          style: this.root.dataset.mapStyle,
          bounds: FRANCE,
          fitBoundsOptions: { padding: 20 },
          cooperativeGestures: true,
          attributionControl: { compact: true },
          dragRotate: false,
          pitchWithRotate: false,
          touchPitch: false,
          locale: {
            'CooperativeGesturesHandler.WindowsHelpText': t.coop_desktop,
            'CooperativeGesturesHandler.MacHelpText': t.coop_mac,
            'CooperativeGesturesHandler.MobileHelpText': t.coop_mobile,
            'NavigationControl.ZoomIn': '+',
            'NavigationControl.ZoomOut': '−',
          },
        });
        map.touchZoomRotate.disableRotation();
        map.addControl(new ml.NavigationControl({ showCompass: false }), 'top-right');
        map.on('load', () => {
          map.addSource('sl', { type: 'geojson', data: { type: 'FeatureCollection', features: [] }, cluster: true, clusterRadius: 46, clusterMaxZoom: 12 });
          map.addLayer({
            id: 'sl-clusters', type: 'circle', source: 'sl', filter: ['has', 'point_count'],
            paint: {
              'circle-color': '#2b2b2c',
              'circle-radius': ['step', ['get', 'point_count'], 15, 10, 18, 50, 21, 200, 25],
              'circle-stroke-color': 'rgba(43,43,44,0.18)',
              'circle-stroke-width': 5,
            },
          });
          map.addLayer({
            id: 'sl-count', type: 'symbol', source: 'sl', filter: ['has', 'point_count'],
            layout: { 'text-field': ['get', 'point_count_abbreviated'], 'text-font': ['Noto Sans Bold'], 'text-size': 12, 'text-allow-overlap': true },
            paint: { 'text-color': '#ffffff' },
          });
          map.addLayer({
            id: 'sl-point', type: 'circle', source: 'sl', filter: ['!', ['has', 'point_count']],
            paint: { 'circle-color': '#2b2b2c', 'circle-radius': 5, 'circle-stroke-color': '#ffffff', 'circle-stroke-width': 2 },
          });
          map.on('click', 'sl-clusters', (e) => {
            const f = e.features[0];
            map.getSource('sl').getClusterExpansionZoom(f.properties.cluster_id).then((z) => {
              map.easeTo({ center: f.geometry.coordinates, zoom: z + 0.5 });
            });
          });
          map.on('click', 'sl-point', (e) => this.select(e.features[0].properties.id, 'map'));
          ['sl-clusters', 'sl-point'].forEach((l) => {
            map.on('mouseenter', l, () => { map.getCanvas().style.cursor = 'pointer'; });
            map.on('mouseleave', l, () => { map.getCanvas().style.cursor = ''; });
          });
          this.map = map;
          this.el.map.classList.add('is-ready');
          resolve(map);
          this.lastMap = null;
          if (this.stores) this.render({ fit: true });
          if (this.state.selected) this.renderSelection('list');
        });
      })).catch(() => { this.mapReady = null; });
      return this.mapReady;
    }

    updateMap(all, { fit, france, around = [], numbered = [] } = {}) {
      const map = this.map;
      if (!map) return;
      const ml = window.maplibregl;
      const key = `${this.state.gamme}|${this.state.brand}`;
      const skip = new Set(numbered.map((s) => s.id));
      const dataKey = key + '|' + [...skip].join(',');
      if (this.lastMap !== dataKey) {
        this.lastMap = dataKey;
        map.getSource('sl').setData({
          type: 'FeatureCollection',
          features: all.filter((s) => !skip.has(s.id)).map((s) => ({ type: 'Feature', geometry: { type: 'Point', coordinates: [s.lng, s.lat] }, properties: { id: s.id } })),
        });
      }

      this.pins.forEach((p) => p.marker.remove());
      this.pins = numbered.map((s, i) => {
        const el = document.createElement('button');
        el.type = 'button';
        el.className = 'sl-pin';
        el.setAttribute('aria-label', s.nom);
        el.innerHTML = `${i + 1}<span class="sl-pin__label">${esc(s.nom)}</span>`;
        el.addEventListener('click', (ev) => { ev.stopPropagation(); this.select(s.id, 'map'); });
        return { id: s.id, el, marker: new ml.Marker({ element: el }).setLngLat([s.lng, s.lat]).addTo(map) };
      });

      if (this.me) { this.me.remove(); this.me = null; }
      const o = this.state.origin;
      if (o) {
        const el = document.createElement('div');
        el.className = 'sl-me';
        el.setAttribute('aria-hidden', 'true');
        this.me = new ml.Marker({ element: el }).setLngLat([o.lng, o.lat]).addTo(map);
      }

      if (!fit) return;
      this.fitted = true;
      if (france || !o) { map.fitBounds(FRANCE, { padding: 20, duration: 0 }); return; }
      const b = new ml.LngLatBounds([o.lng, o.lat], [o.lng, o.lat]);
      around.slice(0, 10).forEach((s) => b.extend([s.lng, s.lat]));
      map.fitBounds(b, { padding: MOBILE.matches ? 48 : 70, maxZoom: 14, duration: 500 });
    }
  }

  window.SmoonStoreLocator = StoreLocator;
  const init = (scope) => (scope || document).querySelectorAll('[data-store-locator]').forEach((r) => {
    if (!r.slInstance) r.slInstance = new StoreLocator(r);
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  /* Éditeur de thème : la section est re-rendue à chaque réglage. */
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
